import React, { useEffect, useRef, useState } from 'react';
import type { ExcelSummary } from '../Types/interfaces';
import {
  GetSummaryOfInternshipProcessesOfUniversitySupervisor,
  ReGradeEvaluationReport,
} from '../helper/apiHelper';
import type { InputRef, TableColumnType, TableProps } from 'antd';
import { Button, Input, InputNumber, Select, Space, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import {
  FilterFilled,
  FilterOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';
import type { JSX } from 'react';
import { useCurrentUser } from '../hooks/useCurrentUser';

const SummaryTable: React.FC = () => {
  const loggedInUser = useCurrentUser();
  const [processes, setProcesses] = useState<ExcelSummary[]>([]);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const { t } = useTranslation();
  const searchInput = useRef<InputRef>(null);
  const [filteredProcesses, setFilteredProcesses] = useState<ExcelSummary[]>(
    []
  );
  const [operator, setOperator] = useState('');
  const [value, setValue] = useState<string>('');

  type DataIndex = keyof ExcelSummary;

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps['confirm'],
    dataIndex: DataIndex
  ): void => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void): void => {
    clearFilters();
    setSearchText('');
  };

  interface FilterDropdownPropsExtended extends FilterDropdownProps {
    setSelectedKeys: (selectedKeys: React.Key[]) => void;
    selectedKeys: React.Key[];
    confirm: FilterDropdownProps['confirm'];
    clearFilters: (() => void) | undefined;
    close: () => void;
  }

  const FilterDropdown: React.FC<
    FilterDropdownPropsExtended & { dataIndex: DataIndex }
  > = ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
    close,
    dataIndex,
  }) => {
    return (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`${t('convSignComp.search')} ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            {t('convSignComp.search')}
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            {t('convSignComp.reset')}
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            {t('convSignComp.filter')}
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            {t('convSignComp.close')}
          </Button>
        </Space>
      </div>
    );
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): TableColumnType<ExcelSummary> => ({
    filterDropdown: (props) => (
      <FilterDropdown
        {...(props as FilterDropdownPropsExtended)}
        dataIndex={dataIndex}
      />
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      JSON.stringify(record[dataIndex])
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const handleGradeChange = (
    event: React.KeyboardEvent,
    index: number
  ): void => {
    const validValues: Array<string> = [
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      '10',
      '',
    ];
    let grade: string = String(processes[index].gradeBySupervisor);
    if (grade === '0') {
      grade = '';
    }

    if (event.key !== 'Backspace' && !validValues.includes(grade + event.key)) {
      event.preventDefault();
      return;
    }

    if (event.key === 'Backspace') {
      grade = grade.substring(0, Math.max(0, grade.length - 1));
    } else {
      grade = grade + event.key;
    }
    const newProcesses: ExcelSummary[] = processes.map(
      (process, processIndex) => {
        if (processIndex === index) {
          process.gradeBySupervisor = Number(grade);
        }
        return process;
      }
    );
    setProcesses(newProcesses);
  };

  const handleLoseFocus = (index: number): void => {
    if (processes[index].gradeBySupervisor === 0) {
      const newProcesses: ExcelSummary[] = processes.map(
        (process, processIndex) => {
          if (processIndex === index) {
            process.gradeBySupervisor = 1;
          }
          return process;
        }
      );
      setProcesses(newProcesses);
    }
  };

  const filtersForStudyLine = Array.from(
    new Set(processes.map((p) => p.studyLineName))
  )
    .filter((slName) => slName)
    .map((slName) => ({
      text: slName,
      value: slName,
    }));

  const filtersForSession = Array.from(
    new Set(processes.map((p) => p.sessionName))
  )
    .filter((sessionName) => sessionName)
    .map((sessionName) => ({
      text: sessionName,
      value: sessionName,
    }));

  const filtersForOrganization = Array.from(
    new Set(processes.map((p) => p.organizationName))
  )
    .filter((orgName) => orgName)
    .map((orgName) => ({
      text: orgName,
      value: orgName,
    }));

  const filtersForStatus = Array.from(new Set(processes.map((p) => p.status)))
    .filter((status) => status)
    .map((status) => ({
      text: status,
      value: status,
    }));

  const numberFilter = (
    dataIndex: DataIndex
  ): TableColumnType<ExcelSummary> => ({
    filterDropdown: ({
      setSelectedKeys,
      confirm,
      clearFilters,
    }): JSX.Element => {
      return (
        <div style={{ padding: 8 }}>
          <Space>
            <Select
              style={{ width: 65 }}
              value={operator}
              options={['=', '>', '>=', '<', '<='].map((val) => ({
                label: val,
                value: val,
              }))}
              onChange={(op) => {
                setOperator(op);
                setSelectedKeys([JSON.stringify({ operator: op, num: value })]);
              }}
            />
            <InputNumber
              placeholder="val"
              value={value}
              onChange={(val) => {
                const v = val ?? '0';
                setValue(v.toString());
                setSelectedKeys([
                  JSON.stringify({ operator, num: v.toString() }),
                ]);
              }}
            />
          </Space>
          <br />
          <Space align="center" style={{ paddingTop: 8 }}>
            <Button
              size="small"
              type="primary"
              onClick={() => {
                confirm();
              }}
            >
              {t('summaryTable.filter')}
            </Button>

            <Button
              size="small"
              onClick={() => {
                setOperator('');
                setValue('');
                setSelectedKeys([JSON.stringify({ operator: '', num: '' })]);
                clearFilters?.();
                confirm();
              }}
            >
              {t('summaryTable.refresh')}
            </Button>
          </Space>
        </div>
      );
    },

    filterIcon: (filtered: boolean) => (
      <FilterOutlined
        style={{ fontSize: '20px', color: filtered ? '#1677ff' : undefined }}
      />
    ),

    onFilter(value, record): boolean {
      const { operator, num } = JSON.parse(value as string);
      const number = Number(num);

      const recordValue = Number(record[dataIndex]);
      switch (operator) {
        case '=':
          return recordValue === number;

        case '>':
          return recordValue > number;

        case '>=':
          return recordValue >= number;

        case '<':
          return recordValue < number;

        case '<=':
          return recordValue <= number;

        default:
          return true;
      }
    },
  });

  const handleFilterChange: TableProps<ExcelSummary>['onChange'] = (
    pagination,
    filters,
    sorter,
    extra
  ) => {
    setFilteredProcesses(extra.currentDataSource);
  };

  const columns: TableColumnType<ExcelSummary>[] = [
    {
      title: t('summaryTable.studentName'),
      dataIndex: 'studentName',
      key: 'studentName',
      width: '20%',
      ...getColumnSearchProps('studentName'),
    },
    {
      title: t('summaryTable.studentEmail'),
      dataIndex: 'studentEmail',
      key: 'studentEmail',
      width: '20%',
      ...getColumnSearchProps('studentEmail'),
    },
    {
      title: t('summaryTable.studyline'),
      dataIndex: 'studyLineName',
      key: 'studyLineName',
      width: '20%',
      filters: filtersForStudyLine,
      onFilter: (value, record) => record.studyLineName === value,
      filterIcon: (filtered: boolean) => (
        <FilterFilled
          style={{ fontSize: '20px', color: filtered ? '#1677ff' : undefined }}
        />
      ),
      filterSearch: true,
    },
    {
      title: t('summaryTable.session'),
      dataIndex: 'sessionName',
      key: 'sessionName',
      width: '20%',
      filters: filtersForSession,
      onFilter: (value, record) => record.sessionName === value,
      filterIcon: (filtered: boolean) => (
        <FilterFilled
          style={{ fontSize: '20px', color: filtered ? '#1677ff' : undefined }}
        />
      ),
      filterSearch: true,
    },
    {
      title: t('summaryTable.organization'),
      dataIndex: 'organizationName',
      key: 'organizationName',
      width: '20%',
      filters: filtersForOrganization,
      onFilter: (value, record) => record.organizationName === value,
      filterIcon: (filtered: boolean) => (
        <FilterFilled
          style={{ fontSize: '20px', color: filtered ? '#1677ff' : undefined }}
        />
      ),
      filterSearch: true,
    },
    {
      title: t('summaryTable.status'),
      dataIndex: 'status',
      key: 'status',
      width: '20%',
      filters: filtersForStatus,
      onFilter: (value, record) => record.status === value,
      filterIcon: (filtered: boolean) => (
        <FilterFilled
          style={{ fontSize: '20px', color: filtered ? '#1677ff' : undefined }}
        />
      ),
      filterSearch: true,
    },
    {
      title: t('summaryTable.supGrade'),
      dataIndex: 'gradeBySupervisor',
      key: 'gradeBySupervisor',
      width: '20%',
      render: (originalGrade, _record, index) => (
        <Input
          variant="borderless"
          maxLength={2}
          value={originalGrade === 0 ? '' : originalGrade}
          onKeyDown={(gradeChangeEven) =>
            handleGradeChange(gradeChangeEven, index)
          }
          onBlur={() => handleLoseFocus(index)}
        />
      ),
      ...numberFilter('gradeBySupervisor'),
    },
    {
      title: t('summaryTable.tutorGrade'),
      dataIndex: 'gradeByTutor',
      key: 'gradeByTutor',
      width: '20%',
      ...numberFilter('gradeByTutor'),
    },
  ];

  const exportJsonToExcel = <T extends ExcelSummary>(
    jsonData: T[],
    fileName: string
  ): void => {
    const worksheet = XLSX.utils.json_to_sheet(jsonData);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const data = new Blob([excelBuffer], { type: 'application/octet-stream' });

    saveAs(data, `${fileName}.xlsx`);
  };

  const saveSupevisorGrades = async (): Promise<void> => {
    processes.forEach(async (process) => {
      await ReGradeEvaluationReport(Number(process.id), {
        finalGrade: process.gradeBySupervisor,
      });
    });
  };

  useEffect(() => {
    const fetchInternshipProcesses = async (): Promise<void> => {
      const processesOfSupervisor =
        await GetSummaryOfInternshipProcessesOfUniversitySupervisor(
          loggedInUser.id
        );
      setProcesses(processesOfSupervisor);
    };
    fetchInternshipProcesses();
  }, [loggedInUser.id]);

  return (
    <>
      <Table<ExcelSummary>
        columns={columns}
        dataSource={processes}
        onChange={handleFilterChange}
      />
      <Button
        type="primary"
        style={{ marginTop: '16px', marginRight: '10px' }}
        onClick={saveSupevisorGrades}
      >
        {t('summaryTable.save')}
      </Button>
      <Button
        onClick={() =>
          exportJsonToExcel(filteredProcesses, 'SummaryOfStudents')
        }
        type="primary"
        style={{ marginTop: '16px' }}
      >
        {t('summaryTable.export')}
      </Button>
    </>
  );
};

export default SummaryTable;
